/**
 * The one canonical serialisation of the `data-inline` attribute.
 *
 * Two places write this attribute: `updateDataInline` when a Text or Product
 * block mounts, and `applyComputedStyles` on TinyMCE's SaveContent. They used
 * to disagree — one emitted `color:rgb(17,17,17)`, the other
 * `color: rgb(17, 17, 17)` — so each undid the other and a sidebar-edited block
 * was rewritten and re-saved on every open of the document, indefinitely.
 * Both go through this module so they cannot drift apart again.
 *
 * https://linear.app/getanewsletter/issue/FE-1359
 */

export const styleNames = [
  'padding',
  'color',
  'font-size',
  'font-family',
  'font-weight',
  'text-align',
  'list-style-position',
]

// `rgb(17, 17, 17)` from getComputedStyle and `rgb(17,17,17)` from the global
// settings are the same colour written two ways, so collapse the space after the
// commas inside the colour functions. Deliberately scoped to those functions
// rather than the whole value: `font-family:Lato, sans-serif` needs its space.
//
// Only after a comma, not all whitespace: CSS Color 4 writes the same colour
// space-separated, and stripping that turns `rgb(17 17 17)` into the invalid
// `rgb(171717)` — black text in an email, the failure FE-1271 exists to prevent.
// A browser serialises computed colours comma-first so this does not arise from
// `getComputedStyle`, but a value parsed back out of a stored `data-inline`
// reaches here untouched whenever the computed value for that key is empty.
export const normalizeStyleValue = (value) =>
  String(value)
    .trim()
    .replace(/rgba?\([^)]*\)/g, (fn) => fn.replace(/,\s+/g, ','))

// A family name may go unquoted only when it is a single CSS identifier.
// Whitespace, a leading digit or punctuation all mean the quotes are carrying the
// name, and stripping them yields a declaration the client throws away — fed
// `3D, sans-serif`, Chrome and Firefox both discard it and fall back to a default.
const FAMILY_IDENT = /^-?[A-Za-z_][A-Za-z0-9_-]*$/

// Quoting these is what distinguishes a family actually named `sans-serif` from
// the generic keyword, so a quoted one keeps its quotes.
const RESERVED_FAMILIES = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'math',
  'emoji',
  'fangsong',
  'inherit',
  'initial',
  'unset',
  'revert',
  'revert-layer',
  'default',
])

// Split on commas outside quotes: a family name may legally contain one
// (`"Foo, Bar"`), and splitting naively turns that single family into two.
const splitFamilies = (value) => {
  const families = []
  let current = ''
  let quote = null

  for (const character of value) {
    if (quote) {
      if (character === quote) quote = null
      current += character
    } else if (character === '"' || character === "'") {
      quote = character
      current += character
    } else if (character === ',') {
      families.push(current)
      current = ''
    } else {
      current += character
    }
  }

  families.push(current)
  return families
}

// Blink and Gecko disagree on how they serialise `font-family`, and both writers
// read it straight from getComputedStyle. Chromium normalises the quoting — it
// quotes a family name needing quotes and strips them from one that does not —
// while Firefox hands back whatever the stylesheet declared. So
// `font-family:DM Sans, sans-serif`, a real stored setting, becomes
// `"DM Sans", sans-serif` in Chrome and stays bare in Firefox, and two browsers
// rewrite each other's documents forever. FE-1364.
//
// Adopting Chromium's rule makes both land on the same string.
export const normalizeFontFamily = (value) =>
  splitFamilies(String(value))
    .map((family) => family.trim())
    .filter(Boolean)
    .map((family) => {
      const quoted = /^(["']).*\1$/.test(family)
      const bare = quoted ? family.slice(1, -1) : family
      const keepQuotes =
        !FAMILY_IDENT.test(bare) ||
        (quoted && RESERVED_FAMILIES.has(bare.toLowerCase()))
      return keepQuotes ? `"${bare}"` : bare
    })
    .join(', ')

// Emitted in `styleNames` order rather than the order of the incoming object,
// so the result cannot depend on the order the previous writer happened to use.
// No trailing `;` — the `setLineHeight`/`setMargin` tails bring their own.
export const serializeDataInline = (styles) =>
  styleNames
    .filter((key) => typeof styles[key] !== 'undefined' && styles[key] !== '')
    .map((key) => {
      const value =
        key === 'font-family'
          ? normalizeFontFamily(styles[key])
          : normalizeStyleValue(styles[key])
      return `${key}:${value}`
    })
    .join(';')

export const serializeLinkDataInline = (color) =>
  `color:${normalizeStyleValue(color)};text-decoration:none`
